import { DataTopic, type ChatMessage, type ChatOptions } from "@livekit/components-core";
import * as React from "react";
import {
  useConnectionState,
  useDataChannel,
  useMaybeLayoutContext,
  useRoomContext,
} from "@livekit/components-react";
import { cloneSingleChild } from "./utils";
import type { MessageFormatter } from "./utils";
import { ChatEntry } from "./ChatEntry";
import { useChat } from "@livekit/components-react";
import { ChatToggle } from "./ChatToggle";
import CloseIcon from "@mui/icons-material/Close";
import "./livekit.css";
import { ConnectionState, DataPacket_Kind } from "livekit-client";
import FileUpload from "../fileUpload/FileUpload";
import { useRecoilValue } from "recoil";
import { fileShareInfosState, oneToOneChatParticipantIdState, staticConfigAtom } from "../../atom/atom";
import ProgressLoader from "../../utils/FileUploadProgressLoader";
import OneToOneChat from "../one-to-one-chat/OneToOneChat";

/** @public */
export interface ChatProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChatOptions {
  messageFormatter?: MessageFormatter;
}

export function Chat({
  messageFormatter,
  messageDecoder,
  messageEncoder,
  channelTopic,
  ...props
}: ChatProps) {

  const isFeatureConfigured = useRecoilValue(staticConfigAtom);
  
  const room = useRoomContext();
  const {send: sendDataMessage } = useDataChannel(DataTopic.CHAT);
  const oneToOneChatParticipantId = useRecoilValue(oneToOneChatParticipantIdState);

  const encoder = new TextEncoder();
  
  const inputRef = React.useRef<HTMLInputElement>(null);
  const ulRef = React.useRef<HTMLUListElement>(null);

  const chatOptions: ChatOptions = React.useMemo(() => {
    return { messageDecoder, messageEncoder, channelTopic };
  }, [messageDecoder, messageEncoder, channelTopic]);

  const { send, chatMessages, isSending } = useChat(chatOptions);
  const connectionState = useConnectionState();

  const layoutContext = useMaybeLayoutContext();
  const lastReadMsgAt = React.useRef<ChatMessage["timestamp"]>(0);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (inputRef.current && inputRef.current.value.trim() !== "") {
      if (send) {
        const  oneToOneMessage = JSON.stringify({
          message: inputRef.current.value,
          timestamp: Date.now()
        });
        console.log('onetoone selected', oneToOneChatParticipantId.isParticipantSelected)
        if(oneToOneChatParticipantId.isParticipantSelected){
          sendDataMessage(encoder.encode(oneToOneMessage) , { kind: DataPacket_Kind.RELIABLE, destination: [oneToOneChatParticipantId.participantId] });
          chatMessages.push({from: room.localParticipant, message: inputRef.current.value, timestamp: Date.now()});
        }else{
          send(inputRef.current.value);
        }
        
        inputRef.current.value = "";
        inputRef.current.focus();
      }
    }
  }
  
  React.useEffect(() => {
    if (ulRef) {
      ulRef.current?.scrollTo({ top: ulRef.current.scrollHeight });
    }
  }, [ulRef, chatMessages]);

  React.useEffect(() => {
    if (!layoutContext || chatMessages.length === 0) {
      return;
    }

    if (
      layoutContext.widget.state?.showChat &&
      chatMessages.length > 0 &&
      lastReadMsgAt.current !== chatMessages[chatMessages.length - 1]?.timestamp
    ) {
      lastReadMsgAt.current = chatMessages[chatMessages.length - 1]?.timestamp;
      return;
    }

    const unreadMessageCount = chatMessages.filter(
      (msg) => !lastReadMsgAt.current || msg.timestamp > lastReadMsgAt.current
    ).length;

    const { widget } = layoutContext;
    if (
      unreadMessageCount > 0 &&
      widget.state?.unreadMessages !== unreadMessageCount
    ) {
      widget.dispatch?.({ msg: "unread_msg", count: unreadMessageCount });
    }
  }, [chatMessages, layoutContext?.widget]);

  const fileShareInfo = useRecoilValue(fileShareInfosState);

  return (
    <div {...props} className="lk-chat">
      <div className="chat-container">
        <span>
          {" "}
          <b>Meeting chat</b>
        </span>
        <ChatToggle className="lk-chat-togglee">
          <CloseIcon />
        </ChatToggle>
      </div>

      <ul className="lk-list lk-chat-messages mb" ref={ulRef}>
        {props.children
          ? chatMessages.map((msg, idx) =>
              cloneSingleChild(props.children, {
                entry: msg,
                key: idx,
                messageFormatter,
              })
            )
          : chatMessages.map((msg, idx, allMsg) => {
              const hideName = idx >= 1 && allMsg[idx - 1].from === msg.from;
              // If the time delta between two messages is bigger than 60s show timestamp.
              const hideTimestamp =
                idx >= 1 && msg.timestamp - allMsg[idx - 1].timestamp < 60_000;

              return (
                <ChatEntry
                  key={idx}
                  hideName={hideName}
                  hideTimestamp={hideName === false ? false : hideTimestamp} // If we show the name always show the timestamp as well.
                  entry={msg}
                  messageFormatter={messageFormatter}
                />
              );
            })}

        {fileShareInfo.map((fileShareInfo) => (
          <li key={fileShareInfo.fileName}>
            <ProgressLoader
              showLoader={fileShareInfo.showLoader}
              fileName={fileShareInfo.fileName}
            />
          </li>
        ))}
      </ul>

      

      <form className="lk-chat-form-container" onSubmit={handleSubmit}>
      {isFeatureConfigured?.features.oneToOneChat && ( 
        <div className="oneToOne-chat-container">
        <span>To:</span> 
        <OneToOneChat />
      </div>)}
     
      <div className="chat-container-flex">
      <input
          className="lk-form-control lk-chat-form-input"
          disabled={isSending}
          ref={inputRef}
          type="text"
          placeholder="Enter a message..."
        />
        {isFeatureConfigured?.features.fileSharing && (<FileUpload />)}
        <button
          type="submit"
          className="lk-button lk-chat-form-button"
          disabled={isSending || connectionState !== ConnectionState.Connected}
        >
          Send
        </button>
      </div>
    
       
      </form>
    </div>
  );
}
